/**
 * Journal > Importer -- review-and-apply UI for backporting the Discord dream logs into
 * `JournalSegment.publicText`.
 *
 * ## Why a review UI instead of a script
 *
 * The parse (`Source/JournalImport/DiscordExport.ts`) is deterministic, but the *target* -- which
 * `JournalEntry` and which segment of it a Discord post belongs to -- is inferred from dates the
 * author only sometimes wrote out. A silent bulk import would therefore put text on the wrong dream
 * with no way to notice. So this tab:
 *
 *  1. parses the export into one "draft" per dream (a draft = a candidate `publicText`);
 *  2. shows the matcher's suggested entry+segment, WHY it suggested it, and the runner-up options;
 *  3. keeps the text editable, and lets the user pick a different segment before applying;
 *  4. applies ONE draft at a time (or a batch of unambiguous ones), never on load.
 *
 * Nothing is written until the user presses "Apply".
 *
 * ## Reading the draft rows
 *
 *  - the date is the inferred morning date, tagged with where it came from (`post` = inferred from the
 *    message timestamp, which is the weakest source);
 *  - warnings come from `Match.ts`: `ambiguous` (two nights scored the same), `dateOff`, a
 *    kind/ordinal mismatch, or `target already filled` (that segment already has publicText);
 *  - "Apply" is only highlighted as confident for unambiguous matches; the batch button only touches those.
 */
import {observer_mgl} from "mobx-graphlink";
import React, {useMemo, useRef, useState} from "react";
import {Button, CheckBox, Column, Row, Select, Spinner, Text, TextArea, TextInput} from "react-vcomponents";
import {ScrollView} from "react-vscrollview";
import {ES, RunInAction_Set, TimeToString} from "web-vcore";
import {store} from "../../Store/index.js";
import {CreateJournalImportDraft, GetDraftApplyText, IsJournalImportDraftExpanded, JournalImportDraft} from "../../Store/main/journalImporter.js";
import {
	DateSource,
	DiscordExportFile,
	DreamGroup,
	FormatDreamDate,
	GroupWarning,
	ParseDiscordExport,
	ParseDreamDateString,
	SkippedLog,
} from "../../JournalImport/DiscordExport.js";
import {
	DEFAULT_MAX_DAY_DIFF,
	GetCandidateEntries,
	GetDreamSegmentsOfEntry,
	GetEntryDreamDate,
	GetPublicTextOverlap,
	GroupMatch,
	IsMatchConfident,
	JournalEntryForImport,
	MatchGroupToSegment,
	MatchWarning,
	PublicTextOverlap,
	SegmentRef,
} from "../../JournalImport/Match.js";
import {ApplyPublicTextToSegment, GetJournalImportEntries} from "../../JournalImport/JournalImport_Live.js";
import {FindClosestSegmentMatch} from "../../JournalImport/ClosestMatch.js";
import {JournalEntry} from "../../Server/DB/journalEntries/@JournalEntry.js";
import {JournalDisplayModeSwitcher} from "./JournalUI.js";

/** The manual entry picker offers a WIDER window than the automatic suggestion, since a backlog post
 * can legitimately reference a night the scoring rejects (and the user is the final authority). */
const ENTRY_PICKER_DAY_WINDOW = 7;
const SKIPPED_LOGS_INITIAL_ROWS = 40;
const SKIPPED_LOGS_PAGE_SIZE = 100;
const MINUTES_PER_HOUR = 60;

export const JournalImporterUI = observer_mgl(()=>{
	const uiState = store.main.journal.importer;
	const entries = GetJournalImportEntries();

	const matches = useMemo(()=>{
		const result = new Map<string, GroupMatch>();
		for (const draft of uiState.drafts) {
			result.set(draft.groupId, MatchGroupToSegment(draft.group, entries, {utcOffsetMinutes: uiState.utcOffsetMinutes}));
		}
		return result;
	}, [uiState.drafts, entries, uiState.utcOffsetMinutes]);

	const visibleDrafts = uiState.drafts.filter(draft=>{
		// each non-pending status has its OWN show flag (previously a skipped draft also required
		// `showApplied`, so ticking "skipped" alone listed nothing)
		if (draft.status == "skipped" && !uiState.showSkipped) return false;
		if (draft.status == "applied" && !uiState.showApplied) return false;
		const match = matches.get(draft.groupId);
		if (!uiState.showNonConfident && !(match != null && IsMatchConfident(match))) return false;
		const search = uiState.filterText.trim().toLowerCase();
		if (search.length > 0) {
			const haystack = `${draft.group.heading}\n${draft.text}\n${draft.group.preamble}`.toLowerCase();
			if (!haystack.includes(search)) return false;
		}
		return true;
	});

	const pendingDrafts = uiState.drafts.filter(a=>a.status == "pending");
	const readyDrafts = pendingDrafts.filter(a=>{
		const match = matches.get(a.groupId);
		return match != null && IsMatchConfident(match);
	});
	const appliedCount = uiState.drafts.filter(a=>a.status == "applied").length;
	const skippedDrafts = uiState.drafts.filter(a=>a.status == "skipped");
	const autoSkippedCount = skippedDrafts.filter(a=>a.skippedAuto).length;

	// Auto-skip drafts whose target already holds 100% of the lines (nothing left to decide on). Done in
	// an effect because it needs the entries, which may still be loading; `statusSignature` re-runs the
	// pass when a draft's status/target changes, and converges since a no-op pass returns early.
	const statusSignature = uiState.drafts
		.map(a=>`${a.status}/${a.targetMode}/${a.manualEntryId ?? ""}/${a.manualSegmentIndex ?? ""}/${a.autoSkipDismissed ? 1 : 0}`)
		.join("|");
	React.useEffect(()=>{
		const toSkip: JournalImportDraft[] = [];
		const toRevive: JournalImportDraft[] = [];
		for (const draft of uiState.drafts) {
			if (draft.autoSkipDismissed) continue;
			const match = matches.get(draft.groupId);
			if (match == null) continue;
			const allMatch = GetDraftOverlap(draft, match, entries, uiState.utcOffsetMinutes)?.allMatch ?? false;
			if (allMatch && draft.status == "pending") toSkip.push(draft);
			else if (!allMatch && draft.status == "skipped" && draft.skippedAuto) toRevive.push(draft);
		}
		if (toSkip.length == 0 && toRevive.length == 0) return; // converge: no write -> no re-render loop

		RunInAction_Set(()=>{
			for (const draft of toSkip) {
				draft.status = "skipped";
				draft.skippedAuto = true;
			}
			for (const draft of toRevive) {
				draft.status = "pending";
				draft.skippedAuto = false;
			}
		});
	}, [uiState.drafts, entries, uiState.utcOffsetMinutes, statusSignature, matches]);

	return (
		<Column style={ES({height: "100%", flex: 30})}>
			<Row className="clickThrough" style={{flexWrap: "wrap", gap: 5, padding: 10, alignItems: "center", background: "rgba(0,0,0,.7)", borderRadius: "10px 10px 0 0"}}>
				{/* the same tab bar the other journal display modes show, so switching away is one click */}
				<JournalDisplayModeSwitcher/>
				<Row style={{gap: 6, alignItems: "center"}}>
					<Text>Show:</Text>
					<CheckBox text="non-confident" value={uiState.showNonConfident} onChange={val=>RunInAction_Set(()=>{ uiState.showNonConfident = val; })} title="Untick to list only the drafts whose match is unambiguous and needs no review before applying."/>
					<CheckBox text="ignored posts" value={uiState.showSkippedLogs} onChange={val=>RunInAction_Set(()=>{ uiState.showSkippedLogs = val; })}/>
					<CheckBox text="skipped" value={uiState.showSkipped} onChange={val=>RunInAction_Set(()=>{ uiState.showSkipped = val; })}/>
					<CheckBox text="applied" value={uiState.showApplied} onChange={val=>RunInAction_Set(()=>{ uiState.showApplied = val; })}/>
				</Row>
				<Row style={{gap: 6, alignItems: "center"}}>
					<Text>Post timezone offset (hours):</Text>
					<Spinner
						value={uiState.utcOffsetMinutes / MINUTES_PER_HOUR}
						onChange={val=>RunInAction_Set(()=>{ uiState.utcOffsetMinutes = Math.round(val * MINUTES_PER_HOUR); })}
						style={{width: 50}}
					/>
					<Text style={{opacity: .6}}>(UTC{uiState.utcOffsetMinutes >= 0 ? "+" : ""}{Math.round(uiState.utcOffsetMinutes / MINUTES_PER_HOUR)})</Text>
				</Row>
				<CheckBox text="Start expanded" value={uiState.startExpanded} onChange={val=>RunInAction_Set(()=>{ uiState.startExpanded = val; })} title="Show each draft's preview by default; collapse the individual ones you have already checked."/>
				<Row ml="auto" style={{gap: 6, alignItems: "center"}}>
					<Text>Filter:</Text>
					<TextInput
						style={{width: 200}}
						value={uiState.filterText}
						onChange={val=>RunInAction_Set(()=>{ uiState.filterText = val; })}
					/>
				</Row>
			</Row>
			<ScrollView style={{flex: 1}} contentStyle={{
				display: "flex",
				flexDirection: "column",
				padding: 10,
				backgroundColor: "rgba(30,30,30,.7)",
				borderRadius: "0 0 10px 10px",
			}}>
				<HeaderRow/>

				<Row mt={10} style={{gap: 8, alignItems: "center"}}>
					<Text>
						{pendingDrafts.length} pending, {readyDrafts.length} ready, {appliedCount} applied, {skippedDrafts.length} skipped
						{autoSkippedCount > 0 ? ` (${autoSkippedCount} auto)` : ""}
					</Text>
					<Button
						ml="auto"
						text={`Apply all ready (${readyDrafts.length})`}
						enabled={readyDrafts.length > 0 && uiState.applyingGroupId == null}
						title="Applies only drafts whose match is unambiguous, exact-date, correct kind+ordinal, and whose target segment is still empty."
						onClick={()=>ApplyDrafts(readyDrafts, matches)}
					/>
				</Row>

				{uiState.applyLog.length > 0 && (
					<Column mt={8} style={{maxHeight: 90, overflowY: "auto", backgroundColor: "rgba(0,0,0,.25)", padding: 6, borderRadius: 4}}>
						{uiState.applyLog.slice(-40).map((line, index)=><Text key={index} style={{fontSize: 12, opacity: .8}}>{line}</Text>)}
					</Column>
				)}

				{uiState.showSkippedLogs && uiState.skippedLogs.length > 0 && <SkippedLogsPanel logs={uiState.skippedLogs}/>}

				<Column mt={10} style={{gap: 10}}>
					{visibleDrafts.map(draft=>{
						const match = matches.get(draft.groupId);
						if (match == null) return null; // drafts were just replaced; the next render has the matches
						return <DraftCard key={draft.groupId} draft={draft} match={match} entries={entries}/>;
					})}
					{visibleDrafts.length == 0 && <Text style={{opacity: .6}}>No drafts match the current filters.</Text>}
				</Column>
			</ScrollView>
		</Column>
	);
});

// header / import
// ==========

const HeaderRow = observer_mgl(()=>{
	const uiState = store.main.journal.importer;
	const fileInputRef = useRef<HTMLInputElement|null>(null);
	const [busy, setBusy] = useState(false);

	const onFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>)=>{
		const files = [...(e.target.files ?? [])];
		e.target.value = ""; // allow re-selecting the same files later
		if (files.length == 0) return;

		setBusy(true);
		try {
			const parsedFiles: DiscordExportFile[] = [];
			for (const file of files) {
				parsedFiles.push(JSON.parse(await file.text()) as DiscordExportFile);
			}
			const result = ParseDiscordExport(parsedFiles, {
				utcOffsetMinutes: uiState.utcOffsetMinutes,
				minPostDate: ParseDreamDateString(uiState.minDate),
			});

			RunInAction_Set(()=>{
				// keep drafts that already exist (their edits/status matter more than a re-parse)
				const existing = new Map(uiState.drafts.map(a=>[a.groupId, a]));
				const drafts = result.groups.map(group=>existing.get(group.groupId) ?? CreateJournalImportDraft(group));
				uiState.drafts = drafts;
				uiState.skippedLogs = result.skipped;
				uiState.importError = null;
				uiState.importSummary =
					`${result.groups.length} dream draft(s) from ${result.stats.messagesFromAuthor} author message(s) `
					+ `in ${result.stats.sessions} posting session(s); ${result.skipped.length} post(s) ignored`
					+ (result.stats.messagesBeforeMinDate > 0 ? `; ${result.stats.messagesBeforeMinDate} post(s) before min-date dropped` : "")
					+ (result.stats.duplicatesRemoved > 0 ? `; ${result.stats.duplicatesRemoved} duplicate message(s) dropped` : "");
			});
		} catch (ex) {
			RunInAction_Set(()=>{
				uiState.importError = `Could not parse the selected file(s): ${(ex as Error).message}`;
			});
		} finally {
			setBusy(false);
		}
	};

	return (
		<Column>
			<Row style={{gap: 8, alignItems: "center"}}>
				<Text style={{fontWeight: "bold"}}>Journal Import -- backport Discord dream logs into `publicText`</Text>
			</Row>
			<Row mt={6} style={{gap: 8, alignItems: "center"}}>
				<Text>Min date:</Text>
				<TextInput
					style={{width: 110}}
					value={uiState.minDate}
					placeholder="YYYY-MM-DD"
					title="Posts from before this date are ignored entirely (not even parsed). Leave blank for no cutoff."
					onChange={val=>RunInAction_Set(()=>{ uiState.minDate = val.trim(); })}
				/>
				<Button size={28} mdIcon="close" title="Clear the min date (no cutoff)." enabled={uiState.minDate.length > 0} onClick={()=>RunInAction_Set(()=>{ uiState.minDate = ""; })}/>
				<Text style={{opacity: .75, fontSize: 12}}>
					{uiState.minDate.length == 0
						? "(no cutoff -- every post in the export is parsed)"
						: ParseDreamDateString(uiState.minDate) == null
							? "(invalid date -- cutoff will be ignored)"
							: `(posts before ${uiState.minDate} are ignored entirely, for next import/file-selection)`}
				</Text>
			</Row>
			<Row mt={6} style={{gap: 8, alignItems: "center"}}>
				<Button text={busy ? "Reading..." : "Select Discrub export file(s)..."} enabled={!busy} onClick={()=>fileInputRef.current?.click()}/>
				<input ref={fileInputRef} type="file" accept="application/json,.json" multiple style={{display: "none"}} onChange={onFilesSelected}/>
				<Button text="Clear" enabled={!busy && uiState.drafts.length > 0} onClick={()=>RunInAction_Set(()=>{
					uiState.drafts = [];
					uiState.expandedOverrides = {};
					uiState.searchResults = {};
					uiState.skippedLogs = [];
					uiState.importSummary = null;
					uiState.importError = null;
					uiState.applyLog = [];
				})}/>
				<Text style={{opacity: .75, fontSize: 12}}>
					{uiState.importSummary ?? "No export loaded. Select all pages of the Discrub JSON export (page boundaries cut through the middle of dreams, so every page is needed)."}
				</Text>
			</Row>
			{uiState.importError != null && <Text mt={4} style={{color: "#e08080"}}>{uiState.importError}</Text>}
		</Column>
	);
});

// one draft
// ==========

const DraftCard = observer_mgl((props: {draft: JournalImportDraft, match: GroupMatch, entries: JournalEntry[]})=>{
	const {draft, match, entries} = props;
	const uiState = store.main.journal.importer;
	const group = draft.group;
	const expanded = IsJournalImportDraftExpanded(uiState, draft.groupId);

	const target = ResolveTarget(draft, match, entries, uiState.utcOffsetMinutes);
	const targetEntry = target == null ? null : entries.find(a=>a.id == target.entryId) ?? null;
	const targetSegment = targetEntry == null || target == null ? null : targetEntry.segments[target.segmentIndex] ?? null;
	const confident = IsMatchConfident(match);
	// how much of what this draft would write is already in the target segment: distinguishes an
	// already-applied import (eg. an interrupted batch that got re-imported) from a real conflict
	const overlap = GetDraftOverlap(draft, match, entries, uiState.utcOffsetMinutes);
	// the entry the two dropdowns are editing: the manual pick when one was made, else the suggestion
	const pickerEntryId = draft.targetMode == "manual" ? draft.manualEntryId : (match.target?.entryId ?? null);
	const pickerSegmentIndex = draft.targetMode == "manual" ? draft.manualSegmentIndex : (match.target?.segmentIndex ?? null);

	const setDraft = (changer: ()=>void)=>RunInAction_Set(changer);
	const toggleExpanded = ()=>RunInAction_Set(()=>{
		uiState.expandedOverrides = {...uiState.expandedOverrides, [draft.groupId]: !expanded};
	});
	// top-right: what the target segment currently contains, relative to what this draft would write
	const targetStatusText = overlap == null || overlap.existingLines == 0
		? "target is empty"
		: `target already has ${target?.existingPublicTextLength ?? 0} chars (${overlap.matchedLines}/${overlap.incomingLines} lines matching)`;

	const background = draft.status == "applied" ? "rgba(60,110,60,.25)"
		: draft.status == "skipped" ? "rgba(120,120,120,.18)"
			: confident ? "rgba(60,90,130,.22)" : "rgba(130,90,40,.20)";

	return (
		<Column sel style={{background: background, border: "1px solid rgba(200,200,220,.12)", borderRadius: 6, padding: 8}}>
			<Row style={{gap: 8, alignItems: "baseline", flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>{FormatDreamDate(group.dreamDate)}</Text>
				<Text style={{opacity: .7, fontSize: 12}}>({DateSourceLabel(group.dreamDateSource)})</Text>
				<Text style={{fontWeight: "bold"}}>{group.heading}</Text>
				<Text style={{opacity: .8, fontSize: 12}}>
					{group.kind} #{group.ordinal} ({group.ordinalSource})
					· {group.sectionTitles.length} section(s) · {group.bulletCount} bullet(s) · {group.wordCount} word(s)
					· posted {TimeToString(group.postTimestamp)}
				</Text>
				<Text ml="auto" style={{opacity: .6, fontSize: 12}}>{targetStatusText}</Text>
			</Row>

			{/* target selection */}
			<Row mt={6} style={{gap: 6, alignItems: "center", flexWrap: "wrap"}}>
				<Text>Target:</Text>
				{target == null
					? <Text style={{color: "#e0b080"}}>no segment selected</Text>
					: <Text>{targetEntry == null ? "(entry missing)" : `${FormatDreamDate(target.dreamDate)} · ${targetEntry.title || "(untitled)"} · ${target.label}`}</Text>}
				<Text style={{opacity: .6, fontSize: 12}}>({TargetModeLabel(draft, match, confident)})</Text>

				<Select
					ml={10}
					displayType="dropdown"
					options={GetEntryOptions(group, entries, draft, uiState.utcOffsetMinutes)}
					value={pickerEntryId}
					onChange={(val: string|n)=>setDraft(()=>{
						draft.targetMode = "manual";
						draft.manualEntryId = val ?? null;
						// preselect the segment that best matches this group, so picking an entry is one click
						const entry = entries.find(a=>a.id == val);
						const segments = entry == null ? [] : GetDreamSegmentsOfEntry(entry as JournalEntryForImport, uiState.utcOffsetMinutes);
						const best = segments.find(a=>a.kind == group.kind && a.ordinalWithinKind == group.ordinal)
							?? segments.find(a=>a.kind == group.kind)
							?? segments[0];
						draft.manualSegmentIndex = best?.segmentIndex ?? null;
					})}
				/>
				<Select
					displayType="dropdown"
					options={GetSegmentOptions(pickerEntryId, entries, uiState.utcOffsetMinutes)}
					value={pickerSegmentIndex}
					onChange={(val: number|n)=>setDraft(()=>{
						draft.targetMode = "manual";
						draft.manualEntryId = pickerEntryId;
						draft.manualSegmentIndex = val ?? null;
					})}
				/>
				{draft.targetMode != "auto" && (
					<Button text="Use suggestion" onClick={()=>setDraft(()=>{
						draft.targetMode = "auto";
						draft.manualEntryId = null;
						draft.manualSegmentIndex = null;
					})}/>
				)}
				<Button text="No target" enabled={draft.targetMode != "none"} onClick={()=>setDraft(()=>{ draft.targetMode = "none"; })}/>
			</Row>

			{/* warnings */}
			{match.warnings.length > 0 && (
				<Row mt={4} style={{gap: 8, flexWrap: "wrap"}}>
					{match.warnings.map(warning=>{
						// `targetHasPublicText` is green when the segment already holds exactly this text
						if (warning == "targetHasPublicText" && overlap != null && overlap.allMatch) {
							return <Text key={warning} style={{color: "#70d070", fontWeight: "bold", fontSize: 12}}>· {WarningLabel(warning, overlap)}</Text>;
						}
						return <Text key={warning} style={{color: "#e0a070", fontSize: 12}}>· {WarningLabel(warning, overlap)}</Text>;
					})}
				</Row>
			)}
			{group.warnings.length > 0 && (
				<Row mt={2} style={{gap: 8, flexWrap: "wrap"}}>
					{group.warnings.map(warning=>{
						// `possibleTruncation` means the dream body may be INCOMPLETE, so it is called out in
						// bold orange rather than the muted grey used for cosmetic/formatting notes
						if (warning == "possibleTruncation") {
							return <Text key={warning} style={{color: "#ff9030", fontWeight: "bold", fontSize: 12}}>· post: {GroupWarningLabel(warning)}</Text>;
						}
						return <Text key={warning} style={{opacity: .6, fontSize: 12}}>· post: {GroupWarningLabel(warning)}</Text>;
					})}
				</Row>
			)}

			{/* actions */}
			<Row mt={6} style={{gap: 6, alignItems: "center"}}>
				<CheckBox text="include preamble" value={draft.includePreamble} onChange={val=>setDraft(()=>{ draft.includePreamble = val; })} title="Prepend the meta-comment above the `# ` heading (eg. 'One lucid this morning:') to publicText."/>
				<Button
					text={draft.status == "applied" ? "Applied" : "Apply"}
					enabled={draft.status != "applied" && target != null && uiState.applyingGroupId == null}
					onClick={()=>ApplyDrafts([draft], new Map([[draft.groupId, match]]))}
				/>
				<Button text="Reset text" enabled={draft.text != group.publicText} onClick={()=>setDraft(()=>{ draft.text = group.publicText; })}/>
				{draft.status != "skipped"
					? <Button text="Skip" onClick={()=>setDraft(()=>{ draft.status = "skipped"; draft.skippedAuto = false; })}/>
					: <Button text="Unskip" title="Bring this draft back into the pending list (and stop it being auto-skipped again)." onClick={()=>setDraft(()=>{ draft.status = "pending"; draft.skippedAuto = false; draft.autoSkipDismissed = true; })}/>}
				{draft.status == "applied" && (
					<Button text="Mark pending" onClick={()=>setDraft(()=>{ draft.status = "pending"; draft.appliedAt = null; draft.skippedAuto = false; draft.autoSkipDismissed = true; })}/>
				)}
				<Row ml="auto" style={{gap: 6, alignItems: "center"}}>
					<ClosestMatchUI draft={draft} entries={entries}/>
					<Button
						text="Search for match"
						title="Scan the whole journal for the segment whose text shares the most lines with this draft. Useful when the date-based suggestion is wrong (or impossible), since it ignores dates entirely."
						onClick={()=>SearchJournalForClosestMatch(draft)}
					/>
					<Button text={expanded ? "Collapse" : "Expand"} onClick={toggleExpanded}/>
				</Row>
			</Row>

			{expanded && (
				<>
					{group.preamble.trim().length > 0 && (
						<Column mt={6} style={{background: "rgba(0,0,0,.2)", padding: 6, borderRadius: 4}}>
							<Text style={{opacity: .7, fontSize: 12}}>Preamble (not written unless "include preamble" is checked):</Text>
							<Text style={{whiteSpace: "pre-wrap", fontSize: 12, opacity: .85}}>{group.preamble}</Text>
						</Column>
					)}
					<Column mt={6}>
						<Text style={{opacity: .7, fontSize: 12}}>publicText to write ({draft.text.length} chars, {group.messageIds.length} message(s)):</Text>
						<TextArea
							style={{border: "1px solid black"}}
							autoSize={true}
							value={draft.text}
							onChange={val=>setDraft(()=>{ draft.text = val; })}
						/>
					</Column>
					{targetSegment != null && (targetSegment.publicText ?? "").trim().length > 0 && (
						<Column mt={6}>
							<Text style={{opacity: .7, fontSize: 12}}>Current publicText of the target segment (will be replaced):</Text>
							<pre style={{whiteSpace: "pre-wrap", fontSize: 12, opacity: .8, maxHeight: 200, overflowY: "auto", margin: 0}}>{targetSegment.publicText}</pre>
						</Column>
					)}
				</>
			)}
		</Column>
	);
});

const SkippedLogsPanel = observer_mgl((props: {logs: SkippedLog[]})=>{
	const [limit, setLimit] = useState(SKIPPED_LOGS_INITIAL_ROWS);
	const shown = props.logs.slice(0, limit);
	return (
		<Column mt={10} style={{background: "rgba(0,0,0,.22)", padding: 8, borderRadius: 6}}>
			<Row style={{gap: 8, alignItems: "center"}}>
				<Text style={{fontWeight: "bold"}}>Ignored posts ({props.logs.length})</Text>
				<Text style={{opacity: .6, fontSize: 12}}>
					Nothing is imported from these. Most are ordinary thread conversation; `no heading` means the post had dream content but no `# Lucid`-style heading, which this tool deliberately does not guess at.
				</Text>
			</Row>
			<Column mt={6}>
				{shown.map(log=><Text key={`${log.firstMessageId}`} style={{fontSize: 12, opacity: .75}}>
					{TimeToString(log.timestamp)} · {log.reason}{log.heading ? ` (${log.heading})` : ""} · {log.preview}
				</Text>)}
			</Column>
			{props.logs.length > shown.length && (
				<Button mt={4} text={`Show more (${props.logs.length - shown.length})`} onClick={()=>setLimit(limit + SKIPPED_LOGS_PAGE_SIZE)}/>
			)}
		</Column>
	);
});

// closest-match search ("Search for match" / "X/Y (jump)")
// ==========

/** Runs the whole-journal line-overlap search for one draft, and stores the result so the card can
 * show "X/Y (jump)" until the next search. Deliberately independent of the date-based suggestion: it
 * is the fallback for when the date is unknown or plainly wrong. */
function SearchJournalForClosestMatch(draft: JournalImportDraft) {
	const uiState = store.main.journal.importer;
	// "the entire journal": every entry the Journal page itself lists, not just the date-window candidates
	const result = FindClosestSegmentMatch(GetDraftApplyText(draft), GetJournalImportEntries());
	RunInAction_Set(()=>{
		uiState.searchResults = {...uiState.searchResults, [draft.groupId]: result};
	});
}

/** The "X/Y (jump)" button shown to the LEFT of "Search for match" once a search has been run. */
const ClosestMatchUI = observer_mgl((props: {draft: JournalImportDraft, entries: JournalEntry[]})=>{
	const uiState = store.main.journal.importer;
	const result = uiState.searchResults[props.draft.groupId];
	if (result === undefined) return null; // not searched since the last Clear

	if (result == null) {
		return <Text style={{opacity: .6, fontSize: 12}}>(no journal text to match against)</Text>;
	}

	const entry = props.entries.find(a=>a.id == result.entryId) ?? null;
	const title = (entry?.title ?? "").trim() || "(untitled)";
	// the entry's own dream-date (not the matched segment's), so the tooltip says when this night happened
	const entryDate = entry == null ? null : GetEntryDreamDate(entry as JournalEntryForImport, uiState.utcOffsetMinutes);
	const fieldLabel = result.field == "publicText" ? "publicText" : result.field;
	const tooltip = [
		`Best match: ${result.matchedLines}/${result.incomingLines} line(s) shared`,
		`with ${fieldLabel} of segment ${result.segmentIndex} in "${title}" (${FormatDreamDate(entryDate)}).`,
		`(that segment has ${result.existingLines} line(s) total${result.extraExistingLines > 0 ? `, ${result.extraExistingLines} not in this draft` : ""})`,
		result.allMatch ? "Every line of this draft is already there." : "",
		"",
		`Click to open it at /journal/${result.entryId}.`,
	].filter(a=>a.length > 0).join("\n");

	return (
		<Button
			text={`${result.matchedLines}/${result.incomingLines} (jump)`}
			// green when the draft is fully present already, so "nothing left to do" is visible at a glance
			style={result.allMatch ? {color: "#70d070"} : null}
			enabled={entry != null}
			title={tooltip}
			onClick={()=>RunInAction_Set(()=>{
				store.main.page = "journal";
				store.main.journal.selectedJournalEntry = result.entryId;
			})}
		/>
	);
});

// target resolution
// ==========

/** The segment a draft will write to, after applying the user's override (if any). */
function ResolveTarget(draft: JournalImportDraft, match: GroupMatch, entries: JournalEntry[], utcOffsetMinutes: number): SegmentRef|null {
	if (draft.targetMode == "none") return null;
	if (draft.targetMode == "auto") return match.target ?? null;

	const entry = entries.find(a=>a.id == draft.manualEntryId);
	if (entry == null || draft.manualSegmentIndex == null) return null;
	return GetDreamSegmentsOfEntry(entry as JournalEntryForImport, utcOffsetMinutes)
		.find(a=>a.segmentIndex == draft.manualSegmentIndex) ?? null;
}

/** Overlap between what this draft would write and what its resolved target segment already holds.
 * Null when there is no target (or its entry/segment is gone). Used by BOTH the card's readout and the
 * auto-skip pass, so the two can never disagree about whether a draft is "already applied". */
function GetDraftOverlap(draft: JournalImportDraft, match: GroupMatch, entries: JournalEntry[], utcOffsetMinutes: number): PublicTextOverlap|null {
	const target = ResolveTarget(draft, match, entries, utcOffsetMinutes);
	if (target == null) return null;
	const entry = entries.find(a=>a.id == target.entryId);
	const segment = entry?.segments[target.segmentIndex];
	if (segment == null) return null;
	return GetPublicTextOverlap(GetDraftApplyText(draft), segment.publicText ?? "");
}

function GetEntryOptions(group: DreamGroup, entries: JournalEntry[], draft: JournalImportDraft, utcOffsetMinutes: number) {
	const candidates = GetCandidateEntries(group, entries as JournalEntryForImport[], {utcOffsetMinutes, maxDayDiff: ENTRY_PICKER_DAY_WINDOW});
	const options = [
		{name: "(auto -- none)", value: null as string|n},
		...candidates.map(candidate=>({
			name: `${FormatDreamDate(candidate.dreamDate)}${candidate.dayDiff != 0 ? ` (${candidate.dayDiff > 0 ? "+" : ""}${candidate.dayDiff}d)` : ""}`
				+ ` · ${(candidate.entry.title ?? "").trim() || "(untitled)"}`
				+ ` · ${candidate.dreamSegmentCount} dream(s)`,
			value: candidate.entry.id as string|n,
		})),
	];
	// keep the current manual choice selectable even if it fell outside the window
	if (draft.manualEntryId != null && !options.some(a=>a.value == draft.manualEntryId)) {
		options.push({name: `(kept) ${draft.manualEntryId}`, value: draft.manualEntryId});
	}
	return options;
}

function GetSegmentOptions(entryId: string|n, entries: JournalEntry[], utcOffsetMinutes: number) {
	const entry = entries.find(a=>a.id == entryId);
	if (entry == null) return [{name: "(pick an entry first)", value: null as number|n}];
	const segments = GetDreamSegmentsOfEntry(entry as JournalEntryForImport, utcOffsetMinutes);
	return [
		{name: "(none)", value: null as number|n},
		...segments.map(segment=>({name: segment.label, value: segment.segmentIndex as number|n})),
	];
}

// applying
// ==========

async function ApplyDrafts(drafts: JournalImportDraft[], matches: Map<string, GroupMatch>) {
	const uiState = store.main.journal.importer;
	const entries = GetJournalImportEntries();
	const log = (line: string)=>RunInAction_Set(()=>{ uiState.applyLog.push(line); });

	if (uiState.applyingGroupId != null) return; // one apply at a time, so the log stays readable
	RunInAction_Set(()=>{ uiState.applyingGroupId = "(batch)"; });

	try {
		for (const draft of drafts) {
			const match = matches.get(draft.groupId);
			if (match == null) continue;
			const target = ResolveTarget(draft, match, entries, uiState.utcOffsetMinutes);
			if (target == null) {
				log(`SKIP  ${draft.group.heading} (${FormatDreamDate(draft.group.dreamDate)}): no target segment`);
				continue;
			}

			// re-read the entry from the live list, so a concurrent edit cannot be clobbered by stale data
			const entry = entries.find(a=>a.id == target.entryId);
			if (entry == null) {
				log(`SKIP  ${draft.group.heading}: entry ${target.entryId} vanished`);
				continue;
			}

			// same helper the review UI uses to compare against the segment's existing text
			const text = GetDraftApplyText(draft);

			try {
				RunInAction_Set(()=>{ uiState.applyingGroupId = draft.groupId; });
				await ApplyPublicTextToSegment(entry, target.segmentIndex, text);
				RunInAction_Set(()=>{
					draft.status = "applied";
					draft.appliedAt = Date.now();
					draft.skippedAuto = false;
				});
				log(`OK    ${draft.group.heading} (${FormatDreamDate(draft.group.dreamDate)}) -> ${target.label} [${text.length} chars]`);
			} catch (ex) {
				log(`ERROR ${draft.group.heading} -> ${target.label}: ${(ex as Error).message}`);
			}
		}
	} finally {
		RunInAction_Set(()=>{ uiState.applyingGroupId = null; });
	}
}

// labels
// ==========

function DateSourceLabel(source: DateSource|n) {
	if (source == "preamble") return "date from preamble";
	if (source == "heading") return "date from heading";
	if (source == "relative") return "date from relative wording";
	if (source == "postTimestamp") return "date inferred from post time";
	return "date unknown";
}

function TargetModeLabel(draft: JournalImportDraft, match: GroupMatch, confident: boolean) {
	if (draft.targetMode == "none") return "no target";
	if (draft.targetMode == "manual") return "manual";
	return `suggested${confident ? "" : ", unverified"} (score ${match.score})`;
}

function WarningLabel(warning: MatchWarning, overlap: PublicTextOverlap|n = null) {
	if (warning == "noCandidate") return `no candidate entry within ${DEFAULT_MAX_DAY_DIFF} days`;
	if (warning == "dateOffByNDays") return "candidate is not the same date";
	if (warning == "kindMismatch") return "candidate lucidity-kind differs from the post";
	if (warning == "ordinalMismatch") return "candidate is not the post's ordinal of that kind";
	if (warning == "noSegmentOfKind") return "entry has no segment of that lucidity kind";
	if (warning == "targetHasPublicText") {
		if (overlap == null) return "target already has publicText";
		if (overlap.allMatch) return `target already has publicText (all ${overlap.incomingLines}/${overlap.incomingLines} lines matching -- already applied?)`;
		const extra = overlap.extraExistingLines > 0 ? `, ${overlap.extraExistingLines} other line(s) present` : "";
		return `target already has publicText (${overlap.matchedLines}/${overlap.incomingLines} lines matching${extra})`;
	}
	if (warning == "ambiguous") return "two nights scored the same";
	return warning;
}

function GroupWarningLabel(warning: GroupWarning) {
	if (warning == "possibleTruncation") return "possibly TRUNCATED -- the next post in the thread opens mid-dream (section heading / continuation), so later content was probably cut off; check the 'ignored posts' list for its tail";
	if (warning == "noSections") return "no `### ` sections (prose-only post)";
	if (warning == "noBullets") return "no bullets";
	if (warning == "tinyBody") return "very short body";
	if (warning == "preambleHasBullets") return "preamble contains bullets -- check for an unsectioned summary";
	if (warning == "dateFromPostTimestamp") return "no explicit date written";
	if (warning == "dateUnknown") return "timestamp unparseable";
	return warning;
}
